import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

import { PopulatedKlydo } from '../../../api/klydo';
import { useNavigate, useParams } from 'react-router-dom';
import useUpsertCollectionMutation from './useUpsertCollectionMutation';
import useAutoSave from '../../hooks/useAutosave';
import useAppContext from '../../../hooks/useAppContext';
import useCollectionSubmitValidation from './useCollectionSubmitValidation';
import {
  PREVIEW_GENERATION_FAILED_ALERT,
  UPLOAD_FILE_FAILED_ALERT,
} from '../../../consts/alerts';
import { AlertType } from '../../../design-system/DSAlert';
import useCollectionQuery from './useCollectionQuery';
import useDeleteCollection from './useDeleteCollection';
import useUploadCollectionCoverMutation from '../../hooks/useUploadCollectionCoverMutation';
import useUploadCollectionLogoMutation from '../../hooks/useUploadCollectionLogoMutation';
import useUploadCollectionLogotypeMutation from '../../hooks/useUploadCollectionLogotypeMutation';
import useUploadCollectionThumbnailBackgroundMutation from '../../hooks/useUploadCollectionThumbnailBackgroundMutation';
import {
  DraftCollection,
  CollectionReviewStates,
  CollectionType,
  MarketplaceStatus,
} from '../../../api/collections';
import { useShopifyPreviewUrlQuery } from './useShopifyPreviewUrlQuery';
import usePublishCollection from './usePublishCollection';
import useAnalytics from '../../../hooks/useAnalytics';
import {
  PendulumLogoType,
  PENDULUM_LOGO_TYPE_DEFAULT,
} from '../../../consts/pendulumLogo';

const useCreateCollection = () => {
  const navigate = useNavigate();
  const { showAlert, isAdmin } = useAppContext();
  const { trackEvent } = useAnalytics();
  const { mutate: updateCollectionMutation, isPending: isUpdatingCollection } =
    useUpsertCollectionMutation();
  const { collectionId } = useParams();
  const { data, isLoading } = useCollectionQuery(collectionId!);
  // Approval bakes pendulumLogoSize into logoUrl exactly once (see klydo-api,
  // bakePendulumLogoAtSeal) and can't run again - the api stops returning the
  // field at all once a collection is approved. Computed once here so the
  // size picker's gating (#3), the save payload (#2 below) and the live
  // preview all agree on the same draft-vs-approved verdict.
  const isApprovedCollection = data?.type === CollectionType.Approved;
  const [isCollectionHoldPendulumLogo, setIsCollectionHoldPendulumLogo] =
    useState(data?.holdPendulumLogo || false);
  // null means "no size chosen" (never set, or explicitly Reset) - the API
  // treats this the same as an untouched collection and falls back to its own
  // legacy default, so this never needs to guess a concrete size client-side.
  const [collectionPendulumLogoSize, setCollectionPendulumLogoSize] = useState<
    number | null
  >(
    (data?.type === CollectionType.Draft ? data.pendulumLogoSize : null) ??
      null,
  );
  // 'sized' (pad to pendulumLogoSize) vs 'full' (cover-crop the whole bob) -
  // no "unset" state, unlike pendulumLogoSize: the api defaults to 'sized'
  // when the field has never been set, so the client mirrors that default
  // rather than modeling a third value.
  //
  // Unlike pendulumLogoSize, this is NOT draft-only: approval never erases it
  // (tryClaimApproval's $set only touches type/reviewStatus/marketplace/logoUrl),
  // and Pendulum.tsx still needs it post-approval to decide whether the baked
  // logoUrl should render tinted ('sized') or in its own true colors ('full').
  // Gating this to draft-only would make Studio's preview of an approved
  // 'full' collection show it tinted while the real device renders it correctly.
  const [collectionPendulumLogoType, setCollectionPendulumLogoType] =
    useState<PendulumLogoType>(
      data?.pendulumLogoType ?? PENDULUM_LOGO_TYPE_DEFAULT,
    );
  const [collectionName, setCollectionName] = useState(data?.name || '');
  const [collectionDescription, setCollectionDescription] = useState(
    data?.description || '',
  );
  const [collectionCover, setCollectionCover] = useState(data?.coverUrl || '');
  const [collectionLogo, setCollectionLogo] = useState(data?.logoUrl || '');
  const [collectionLogotype, setCollectionLogotype] = useState(
    data?.productPageMetadata?.logoTypeUrl || '',
  );
  const [collectionThumbnailBackground, setCollectionThumbnailBackground] =
    useState(data?.productPageMetadata?.thumbnailBackgroundUrl || '');
  const [
    collectionThumbnailContrastColor,
    setCollectionThumbnailContrastColor,
  ] = useState(data?.productPageMetadata?.badgeColorHex || '');
  const [collectionKlydoIds, setCollectionKlydoIds] = useState<string[]>(
    data?.klydoIds || [],
  );
  const [collectionKlydos, setCollectionKlydos] = useState<PopulatedKlydo[]>(
    data?.klydos || [],
  );
  const [collectionLabels, setCollectionLabels] = useState<string[]>(
    data?.productPageMetadata?.labels || [],
  );
  const [collectionMockTemplateId, setCollectionMockTemplateId] = useState(
    data?.productPageMetadata?.mockTemplateId || '',
  );
  const [collectionThumbnailKlydos, setCollectionThumbnailKlydos] = useState<
    string[]
  >(data?.productPageMetadata?.thumbnailKlydos || []);
  const [collectionCredits, setCollectionCredits] = useState(
    data?.productPageMetadata?.credits || '',
  );
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(
    data?.paymentDetails?.planId || null,
  );
  const updatedAt = data?.updatedAt || new Date();

  /** Staged only; `handleWarningConfirm` sends it with the request, which stores it. */
  const [reviewComment, setReviewComment] = useState(
    data?.reviewStatus?.comment ?? '',
  );

  const [activeTab, setActiveTab] = useState(0);

  const onDeleteSuccess = () => {
    navigate('/collections');
  };
  const [deletedCollectionId, setDeletedCollectionId] = useState<string>('');
  const { isDeleting, handleDeleteCollection } = useDeleteCollection(
    deletedCollectionId,
    onDeleteSuccess,
  );

  const [isPickModalOpen, setIsPickModalOpen] = useState(false);
  const [
    profileIncompleteErrorDialogOpen,
    setProfileIncompleteErrorDialogOpen,
  ] = useState<boolean>(false);

  const {
    warningDialogOpen,
    isNameTaken,
    handlePublishClick,
    handleWarningConfirm,
    handleWarningClose,
    warningMessage,
    nameWarningNote,
  } = usePublishCollection(collectionId, collectionName, {
    onBeforeOpen: () => {
      if (isProfileMissingFields && !isAdmin) {
        setProfileIncompleteErrorDialogOpen(true);
        return false;
      }
      return true;
    },
    onSuccess: () => navigate('/collections'),
    comment: reviewComment,
  });

  const collectionData = useMemo(
    () => ({
      name: collectionName,
      coverUrl: collectionCover,
      logoUrl: collectionLogo,
      klydoIds: collectionKlydoIds,
      description: collectionDescription,
      productPageMetadata: {
        logoTypeUrl: collectionLogotype,
        thumbnailBackgroundUrl: collectionThumbnailBackground,
        badgeColorHex: collectionThumbnailContrastColor,
        labels: collectionLabels,
        thumbnailKlydos: collectionThumbnailKlydos,
        mockTemplateId: collectionMockTemplateId,
        credits: collectionCredits,
      },
      ...(selectedPlanId && { paymentDetails: { planId: selectedPlanId } }),
    }),
    [
      collectionName,
      collectionCover,
      collectionLogo,
      collectionKlydoIds,
      collectionDescription,
      collectionLogotype,
      collectionThumbnailBackground,
      collectionThumbnailContrastColor,
      collectionLabels,
      collectionThumbnailKlydos,
      collectionMockTemplateId,
      collectionCredits,
      selectedPlanId,
    ],
  );

  const {
    data: cachedPreviewData,
    refetch: fetchPreviewUrl,
    isFetching: isFetchingPreviewUrl,
  } = useShopifyPreviewUrlQuery(collectionId, collectionData);

  const isInMarketplace = data?.marketplace?.status === MarketplaceStatus.True;

  const hasAffiliateLink =
    !!data?.productPageMetadata?.affiliateBundleShopifyProductId;

  const bundleCartImage = data?.productPageMetadata?.bundleProductCartImage;

  const { mutate: uploadCollectionCover, isPending: isUploadingCover } =
    useUploadCollectionCoverMutation({
      onError: () => {
        showAlert({
          message: UPLOAD_FILE_FAILED_ALERT,
          alertType: AlertType.ERROR,
        });
      },
    });

  const { mutate: uploadCollectionLogo, isPending: isUploadingLogo } =
    useUploadCollectionLogoMutation({
      onError: () => {
        showAlert({
          message: UPLOAD_FILE_FAILED_ALERT,
          alertType: AlertType.ERROR,
        });
      },
    });

  const { mutate: uploadCollectionLogotype, isPending: isUploadingLogotype } =
    useUploadCollectionLogotypeMutation({
      onError: () => {
        showAlert({
          message: UPLOAD_FILE_FAILED_ALERT,
          alertType: AlertType.ERROR,
        });
      },
    });

  const {
    mutate: uploadCollectionThumbnailBackground,
    isPending: isUploadingThumbnailBackground,
  } = useUploadCollectionThumbnailBackgroundMutation({
    onError: () => {
      showAlert({
        message: UPLOAD_FILE_FAILED_ALERT,
        alertType: AlertType.ERROR,
      });
    },
  });

  // `collectionKlydos` is seeded from `data` once, so a klydo the artist just
  // finished in its own editor stays stale there; validate the refetched copy.
  const latestCollectionKlydos = useMemo(() => {
    const refetchedById = new Map(
      (data?.klydos ?? []).filter(Boolean).map((klydo) => [klydo.id, klydo]),
    );
    return collectionKlydos.map(
      (klydo) => (klydo && refetchedById.get(klydo.id)) || klydo,
    );
  }, [collectionKlydos, data?.klydos]);

  const { isSubmitDisabled, isProfileMissingFields, handleMissingFields } =
    useCollectionSubmitValidation(
      collectionData,
      isUpdatingCollection,
      isUploadingCover ||
        isUploadingLogo ||
        isUploadingLogotype ||
        isUploadingThumbnailBackground,
      latestCollectionKlydos,
    );

  const handleOpenPickModal = () => {
    setIsPickModalOpen(true);
  };

  const handleClosePickModal = () => {
    setIsPickModalOpen(false);
  };

  const handleKlydoCardClick = useCallback(
    (klydo: PopulatedKlydo) => {
      navigate(`/collection/${collectionId}/klydo/${klydo.id}`);
    },
    [collectionId, navigate],
  );

  const handlePickKlydos = (selectedKlydos: PopulatedKlydo[]) => {
    const newKlydoIds = selectedKlydos.map((klydo) => klydo.id);
    setCollectionKlydoIds(newKlydoIds);
    setCollectionKlydos(selectedKlydos);
    setCollectionThumbnailKlydos(
      collectionThumbnailKlydos.filter((id) => newKlydoIds.includes(id)),
    );
  };

  const handleRemoveKlydo = (klydoId: string) => {
    setCollectionKlydoIds((prevIds) => prevIds.filter((id) => id !== klydoId));
    setCollectionKlydos((prevKlydos) =>
      prevKlydos.filter((klydo) => klydo.id !== klydoId),
    );
    setCollectionThumbnailKlydos(
      collectionThumbnailKlydos.filter((id) => id !== klydoId),
    );
  };

  const handleOrderChange = useCallback((reorderedKlydos: PopulatedKlydo[]) => {
    setCollectionKlydos(reorderedKlydos as PopulatedKlydo[]);
    setCollectionKlydoIds(reorderedKlydos.map((k) => k.id));
  }, []);

  const handleAddLabel = useCallback((label: string) => {
    if (label.trim()) {
      setCollectionLabels((prev) => [...prev, label.trim()]);
    }
  }, []);

  const handleRemoveLabel = useCallback((index: number) => {
    setCollectionLabels((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCoverUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onloadend = () => {
        uploadCollectionCover(
          { file: reader.result as string },
          {
            onSuccess: (response) => {
              setCollectionCover(response.url);
            },
          },
        );
      };
      reader.readAsDataURL(file);
    },
    [uploadCollectionCover],
  );

  const handleLogoUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onloadend = () => {
        uploadCollectionLogo(
          { file: reader.result as string },
          {
            onSuccess: (response) => {
              setCollectionLogo(response.url);
              // A new logo hasn't been sized by the artist yet - carrying over
              // the old logo's size would silently apply it to different art.
              setCollectionPendulumLogoSize(null);
            },
          },
        );
      };
      reader.readAsDataURL(file);
    },
    [uploadCollectionLogo],
  );

  const handleLogotypeUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onloadend = () => {
        uploadCollectionLogotype(
          { file: reader.result as string },
          {
            onSuccess: (response) => {
              setCollectionLogotype(response.url);
            },
          },
        );
      };
      reader.readAsDataURL(file);
    },
    [uploadCollectionLogotype],
  );

  const handleThumbnailBackgroundUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onloadend = () => {
        uploadCollectionThumbnailBackground(
          { file: reader.result as string },
          {
            onSuccess: (response) => {
              setCollectionThumbnailBackground(response.url);
            },
          },
        );
      };
      reader.readAsDataURL(file);
    },
    [uploadCollectionThumbnailBackground],
  );

  const handleCoverRemove = useCallback(() => {
    setCollectionCover('');
  }, []);

  const handleLogoRemove = useCallback(() => {
    setCollectionLogo('');
    setCollectionPendulumLogoSize(null);
  }, []);

  const handleLogotypeRemove = useCallback(() => {
    setCollectionLogotype('');
  }, []);

  const handleThumbnailBackgroundRemove = useCallback(() => {
    setCollectionThumbnailBackground('');
    setCollectionThumbnailContrastColor('');
  }, []);

  const handleAutoSaveTextFields = useCallback(() => {
    if (!collectionId) return;

    const updatedCollection: Partial<DraftCollection> = {
      name: collectionName || 'Untitled',
      description: collectionDescription,
      // No `message`: every autosave asserts `draft`, and a blank note would wipe the review.
      reviewStatus: {
        state: CollectionReviewStates.Draft,
        comment: reviewComment,
      },
    };

    updateCollectionMutation({ collectionId, collection: updatedCollection });
  }, [
    collectionId,
    collectionName,
    collectionDescription,
    reviewComment,
    updateCollectionMutation,
  ]);

  const handleAutoSaveOtherFields = useCallback(() => {
    if (!collectionId) return;

    const updatedCollection: Partial<DraftCollection> = {
      coverUrl: collectionCover,
      logoUrl: collectionLogo,
      klydoIds: collectionKlydoIds,
      reviewStatus: { state: CollectionReviewStates.Draft },
      holdPendulumLogo: isCollectionHoldPendulumLogo,
      // Once approved, the api bakes the size/type into logoUrl and no
      // longer accepts these fields on the collection at all - never send
      // them.
      ...(!isApprovedCollection && {
        pendulumLogoSize: collectionPendulumLogoSize,
        pendulumLogoType: collectionPendulumLogoType,
      }),
      productPageMetadata: {
        ...data?.productPageMetadata,
        labels: collectionLabels,
        logoTypeUrl: collectionLogotype,
        thumbnailBackgroundUrl: collectionThumbnailBackground,
        badgeColorHex: collectionThumbnailContrastColor,
        mockTemplateId: collectionMockTemplateId,
        thumbnailKlydos: collectionThumbnailKlydos,
        credits: collectionCredits,
      },
      ...(selectedPlanId && { paymentDetails: { planId: selectedPlanId } }),
    };

    updateCollectionMutation({
      collectionId,
      collection: updatedCollection,
    });
  }, [
    collectionId,
    collectionCover,
    collectionLogo,
    collectionLogotype,
    collectionThumbnailBackground,
    collectionThumbnailContrastColor,
    collectionKlydoIds,
    collectionLabels,
    isCollectionHoldPendulumLogo,
    collectionPendulumLogoSize,
    collectionPendulumLogoType,
    collectionMockTemplateId,
    collectionThumbnailKlydos,
    collectionCredits,
    selectedPlanId,
    isApprovedCollection,
    data?.productPageMetadata,
    updateCollectionMutation,
  ]);

  const handleCreateShopifyPreviewUrl = useCallback(async () => {
    trackEvent({
      type: 'clicked_collection_product_page',
      context: collectionId,
    });

    if (!collectionId) {
      return;
    }

    let previewUrl = cachedPreviewData?.previewUrl;

    if (!previewUrl) {
      const result = await fetchPreviewUrl();
      if (result.error || !result.data?.previewUrl) {
        showAlert({
          message: PREVIEW_GENERATION_FAILED_ALERT,
          alertType: AlertType.ERROR,
        });
        return;
      }
      previewUrl = result.data.previewUrl;
    }

    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  }, [collectionId, cachedPreviewData, fetchPreviewUrl, showAlert, trackEvent]);

  const { isSaving: isSavingDebounced } = useAutoSave({
    onSave: handleAutoSaveTextFields,
    dependencies: [collectionName, collectionDescription, reviewComment],
  });

  const { isSaving: isSavingImmediate } = useAutoSave({
    onSave: handleAutoSaveOtherFields,
    dependencies: [
      collectionCover,
      collectionLogo,
      collectionLogotype,
      collectionThumbnailBackground,
      collectionThumbnailContrastColor,
      collectionKlydoIds,
      collectionLabels,
      isCollectionHoldPendulumLogo,
      collectionPendulumLogoSize,
      collectionPendulumLogoType,
      collectionMockTemplateId,
      collectionThumbnailKlydos,
      collectionCredits,
      selectedPlanId,
    ],
    delay: 0,
  });

  const isSaving = isSavingDebounced || isSavingImmediate;

  const menuButtonItemsList = useMemo(() => {
    return [
      {
        title: 'Publish',
        isDisabled: isSubmitDisabled,
        onClick: () => handlePublishClick(),
      },

      {
        title: 'Delete',
        isDisabled: false,
        onClick: () => {
          setDeletedCollectionId(collectionId!);
        },
      },
    ];
  }, [handlePublishClick, isSubmitDisabled, collectionId]);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (data && !hasInitialized.current) {
      setCollectionName(data.name || '');
      setCollectionDescription(data.description || '');
      setCollectionCover(data.coverUrl || '');
      setCollectionLogo(data.logoUrl || '');
      setCollectionKlydoIds(data.klydoIds || []);
      setCollectionKlydos(data.klydos || []);
      setCollectionLabels(data.productPageMetadata?.labels || []);
      setIsCollectionHoldPendulumLogo(data.holdPendulumLogo || false);
      setCollectionPendulumLogoSize(
        (data.type === CollectionType.Draft ? data.pendulumLogoSize : null) ??
          null,
      );
      setCollectionPendulumLogoType(
        data.pendulumLogoType ?? PENDULUM_LOGO_TYPE_DEFAULT,
      );
      setCollectionLogotype(data.productPageMetadata?.logoTypeUrl || '');
      setCollectionThumbnailBackground(
        data.productPageMetadata?.thumbnailBackgroundUrl || '',
      );
      setCollectionThumbnailContrastColor(
        data.productPageMetadata?.badgeColorHex || '',
      );
      setCollectionMockTemplateId(
        data.productPageMetadata?.mockTemplateId || '',
      );
      setCollectionThumbnailKlydos(
        data.productPageMetadata?.thumbnailKlydos || [],
      );
      setCollectionCredits(data.productPageMetadata?.credits || '');
      setReviewComment(data.reviewStatus?.comment ?? '');
      hasInitialized.current = true;
    }
  }, [data]);

  const handleTabChange = useCallback((newValue: number) => {
    setActiveTab(newValue);
  }, []);

  return {
    handleCoverUpload,
    handleLogoUpload,
    handleLogotypeUpload,
    handleThumbnailBackgroundUpload,
    handleCoverRemove,
    handleLogoRemove,
    handleLogotypeRemove,
    handleThumbnailBackgroundRemove,
    handlePublishClick,
    isPickModalOpen,
    handleOpenPickModal,
    handleClosePickModal,
    handleRemoveKlydo,
    handlePickKlydos,
    handleOrderChange,
    isSubmitDisabled,
    menuButtonItemsList,
    handleMissingFields,
    profileIncompleteErrorDialogOpen,
    setProfileIncompleteErrorDialogOpen,
    warningDialogOpen,
    handleWarningConfirm,
    handleWarningClose,
    warningMessage,
    nameWarningNote,
    isNameTaken,
    reviewComment,
    setReviewComment,
    // Off the stored record: only a new verdict changes it.
    reviewMessage: data?.reviewStatus?.message,
    isDeleting,
    deletedCollectionId,
    setDeletedCollectionId,
    handleDeleteCollection,
    isLoading,
    collectionName,
    setCollectionName,
    collectionDescription,
    setCollectionDescription,
    collectionCover,
    setCollectionCover,
    collectionLogo,
    setCollectionLogo,
    isCollectionHoldPendulumLogo,
    setIsCollectionHoldPendulumLogo,
    collectionPendulumLogoSize,
    setCollectionPendulumLogoSize,
    collectionPendulumLogoType,
    setCollectionPendulumLogoType,
    isApprovedCollection,
    collectionKlydos,
    collectionLabels,
    handleAddLabel,
    handleRemoveLabel,
    updatedAt,
    handleKlydoCardClick,
    isUploadingCover,
    isUploadingLogo,
    collectionId,
    handleCreateShopifyPreviewUrl,
    isFetchingPreviewUrl,
    isAdmin,
    activeTab,
    handleTabChange,
    collectionLogotype,
    setCollectionLogotype,
    collectionThumbnailBackground,
    setCollectionThumbnailBackground,
    collectionThumbnailContrastColor,
    setCollectionThumbnailContrastColor,
    isUploadingLogotype,
    isUploadingThumbnailBackground,
    uploadCollectionCover,
    uploadCollectionLogo,
    uploadCollectionLogotype,
    uploadCollectionThumbnailBackground,
    canPreviewProductPage: !isSaving || collectionName,
    isInMarketplace,
    hasAffiliateLink,
    bundleCartImage,
    collectionMockTemplateId,
    setCollectionMockTemplateId,
    collectionThumbnailKlydos,
    setCollectionThumbnailKlydos,
    collectionCredits,
    setCollectionCredits,
    selectedPlanId,
    setSelectedPlanId,
  };
};

export default useCreateCollection;
